import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import { QueryClientContext, useMutation, useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useContext, useEffect } from 'react';
import { useIntl } from 'react-intl';

import {
  classifyCuaCloudProviderState,
  CUA_CLOUD_MACHINE_QUERY_KEY,
  cuaCloudClient,
} from './cuaCloud';

/**
 * Embedded live VNC view of the cloud computer. Mints a fresh signed link on
 * mount (links are short-lived) and renders the noVNC iframe, which
 * self-reconnects. Used both by the floating dock and inline in the running
 * computer-use task card so the user watches the agent drive the VM.
 */
export const CloudComputerLiveView = ({
  className,
  autoConnect = true,
}: {
  className?: string;
  autoConnect?: boolean;
}) => {
  const hasQueryClient = useContext(QueryClientContext) !== undefined;
  if (!hasQueryClient) {
    return (
      <LiveViewBody
        className={className}
        autoConnect={autoConnect}
        providerState={undefined}
      />
    );
  }
  return <LiveViewWithMachine className={className} autoConnect={autoConnect} />;
};

const LiveViewWithMachine = ({
  className,
  autoConnect,
}: {
  className?: string;
  autoConnect?: boolean;
}) => {
  const detailsQuery = useQuery({
    queryKey: CUA_CLOUD_MACHINE_QUERY_KEY,
    queryFn: async () => (await cuaCloudClient.getMachineDetails()).data,
    retry: false,
    refetchInterval: (query) => {
      const kind = classifyCuaCloudProviderState(query.state.data?.provider_state);
      return kind === 'running' ? false : 3_000;
    },
  });
  return (
    <LiveViewBody
      className={className}
      autoConnect={autoConnect}
      providerState={detailsQuery.data?.provider_state}
    />
  );
};

const LiveViewBody = ({
  className,
  autoConnect,
  providerState,
}: {
  className?: string;
  autoConnect?: boolean;
  providerState: string | undefined;
}) => {
  const intl = useIntl();
  const vncMutation = useMutation({
    mutationFn: () => cuaCloudClient.createVncLink(),
  });

  const { mutate } = vncMutation;
  useEffect(() => {
    if (autoConnect) mutate();
  }, [autoConnect, mutate]);

  if (vncMutation.data) {
    return (
      <iframe
        title="Your cloud resident live view"
        src={vncMutation.data.data.url}
        className={cn('w-full bg-black', className)}
        allow="clipboard-read; clipboard-write"
        data-dd-privacy="hidden"
      />
    );
  }

  const kind = classifyCuaCloudProviderState(providerState);
  const connectingCopy =
    kind === 'waking'
      ? intl.formatMessage({
          defaultMessage: 'Starting your cloud resident…',
          description: 'Live view while Daytona is creating, starting, or queued',
          id: 'x1LWdfffzC',
        })
      : kind === 'paused'
        ? intl.formatMessage({
            defaultMessage: 'Waking your cloud resident…',
            description: 'Live view while the paused Daytona sandbox is being resumed',
            id: 'PFbgI/yxhv',
          })
        : intl.formatMessage({
            defaultMessage: 'Opening the screen…',
            description: 'Live view while minting the noVNC session',
            id: 'hvX8qvskDM',
          });

  return (
    <div
      className={cn(
        'flex w-full items-center justify-center bg-black/90 text-xs text-white/70',
        className
      )}
    >
      {vncMutation.isError ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-white/70 underline"
          onClick={() => mutate()}
        >
          {intl.formatMessage({
            defaultMessage: "Couldn't connect — retry",
            description: 'Live view retry after the VNC session failed to mint',
            id: 'l8ogs3/jtm',
          })}
        </Button>
      ) : (
        <span className="flex items-center gap-2" data-testid="cloud-computer-connecting">
          <Loader2 className="size-3.5 animate-spin" />
          {connectingCopy}
        </span>
      )}
    </div>
  );
};
